"use client";

import {
  motion,
  useMotionValueEvent,
  useScroll,
  useTransform,
} from "motion/react";
import { useRef, useState } from "react";

import { EASE_OUT_EXPO } from "@/components/motion/reveal";
import { ScrollTextReveal } from "@/components/motion/scroll-text-reveal";
import { usePrefersReducedMotion } from "@/hooks/use-prefers-reduced-motion";

import { DesignIcon, DesktopIcon, MobileIcon, WebIcon } from "./service-icons";

// =============================================================================

const SERVICES = [
  { key: "web", Icon: WebIcon },
  { key: "design", Icon: DesignIcon },
  { key: "mobile", Icon: MobileIcon },
  { key: "desktop", Icon: DesktopIcon },
] as const;

type ServiceKey = (typeof SERVICES)[number]["key"];

/** Share of the pinned scroll spent revealing the words */
const REVEAL_END = 0.62;
/** The services rise once the text is fully revealed */
const SERVICES_AT = 0.6;

type PinnedIntroProps = {
  text: string;
  services: Record<ServiceKey, string>;
};

/**
 * The statement stays pinned in the middle of the screen while its words
 * light up one after another, then the services rise from left to right and
 * the section holds a little before scrolling away.
 */
export function PinnedIntro({ text, services }: PinnedIntroProps) {
  const ref = useRef<HTMLElement>(null);
  const shouldReduceMotion = usePrefersReducedMotion();
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end end"],
  });
  // Function transform: no native scroll acceleration inside the sticky
  const progress = useTransform(scrollYProgress, (value) => value);
  const [showServices, setShowServices] = useState(false);

  useMotionValueEvent(progress, "change", (value) => {
    setShowServices(value >= SERVICES_AT);
  });

  const servicesVisible = shouldReduceMotion || showServices;

  return (
    <section ref={ref} className="relative h-[210svh]">
      <div className="sticky top-0 flex h-svh flex-col items-center justify-center">
        {/* The statement alone is centered: the services sit at the bottom */}
        <div className="container-page">
          <ScrollTextReveal
            text={text}
            progress={progress}
            range={[0.02, REVEAL_END]}
            className="mx-auto max-w-[56rem] justify-center text-center text-[clamp(1.6rem,3.15vw,2.75rem)] leading-[1.2] font-normal tracking-[-0.01em]"
          />

          <motion.ul
            initial={false}
            animate={servicesVisible ? "visible" : "hidden"}
            variants={{
              hidden: {
                transition: { staggerChildren: 0.05, staggerDirection: -1 },
              },
              visible: { transition: { staggerChildren: 0.12 } },
            }}
            className="mx-auto mt-12 grid w-full max-w-[62rem] grid-cols-2 gap-y-10 px-5 sm:absolute sm:inset-x-0 sm:bottom-[11svh] sm:mt-0 sm:grid-cols-4"
          >
            {SERVICES.map(({ key, Icon }) => (
              <motion.li
                key={key}
                variants={{
                  hidden: { opacity: 0, y: 50, filter: "blur(6px)" },
                  visible: {
                    opacity: 1,
                    y: 0,
                    filter: "blur(0px)",
                    transition: { duration: 0.9, ease: EASE_OUT_EXPO },
                  },
                }}
                className="group flex flex-col items-center gap-5 text-center"
              >
                <Icon className="size-10 text-brand transition-transform duration-500 group-hover:-translate-y-1.5 group-hover:rotate-[-8deg]" />
                <span className="text-[clamp(1rem,1.55vw,1.4rem)]">
                  {services[key]}
                </span>
              </motion.li>
            ))}
          </motion.ul>
        </div>
      </div>
    </section>
  );
}

// =============================================================================
